import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import ClearRounded from '@mui/icons-material/ClearRounded';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { secondaryFont } from 'src/theme/typography';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import { inputLabelClasses } from '@mui/material/InputLabel';
import Modal from '@mui/material/Modal';

const useStyles = makeStyles(theme => ({
  loginTextInput: {
    '& .MuiInputBase-input:-webkit-autofill': {
      WebkitTextFillColor: '#212121',
      WebkitBoxShadow: '0 0 0 1000px transparent inset', 
      transition: 'background-color 5000s ease-in-out 0s', 
    },
    '& .MuiFormLabel-root': {
      [`&.${inputLabelClasses.shrink}`]: {
        color: '#212121',
        [`&.${inputLabelClasses.focused}`]: {
          color: '#212121', 
        },
      },
    },
    '& .MuiInputLabel-shrink': {
      display: 'block',
    },
    '& .MuiInputBase-root': {
      background: 'transparent',
    },

    '& .MuiInputBase-input': {
      color: '#212121',
      padding: '13px 10px',
      border:'1px solid #e9e9e9',
      borderRadius: '12px',
      '&::placeholder': {
        opacity: 1,
        color: '#212121',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      fontWeight: 400,
      fontSize: '1.3125rem',
      fontFamily: secondaryFont,
      color: '#212121',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused': {
        borderColor: 'pink',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#e9e9e9',
        },
      },
      '&.Mui-hovered': {
        borderColor: 'blue',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'yellow',
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#a0a0a0',
      '&.Mui-hovered': {
        borderColor: 'purple',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'red',
        },
      },
    },
  },
  
}));

// ----------------------------------------------------------------------

export default function JwtLoginView() {
  const classes = useStyles();

  const { login } = useAuthContext();

  const theme = useTheme();
  const [errorMsg, setErrorMsg] = useState('');
  const [showOrganizationSelection, setShowOrganizationSelection] = useState(false); 
  const [organizationList, setOrganizationList] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [email, setEmail] = useState(null);
  const [pass, setPass] = useState(null);
  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleOrganizationSelection = async (selectedOrgId) => {
    try {
      const response1 = await login?.(email, pass, selectedOrgId);
      if (!response1.success) {
        setErrorMsg('Invalid credentials');
      } else {
        setErrorMsg('');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMsg('An error occurred while logging in.');
    }
  };

  const onSubmit = handleSubmit(async (data,event) => {
    event.preventDefault();
    const response = await login?.(data.email, data.password, organizationId);
    setEmail(data.email);
    setPass(data.password);
    if(response && response.success){
      console.log(response.body.organization_list);
      if (response.body.organization_list && response.body.organization_list.length > 1) {
        setOrganizationList(response.body.organization_list);
        
        setShowOrganizationSelection(true);
      } else {
        setErrorMsg('');
      }
    }else{
      console.log(response);
      setErrorMsg('Invalid credentials');
    }    
  });

  const handleTncChange = (event) => {
    setIsTncChecked(event.target.checked);
  };

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Typography fontSize={30} color="#212121" align="start" fontWeight="600">
        Sign in
      </Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}
      <Stack spacing="30px">
      <div>
        <Typography variant="subtitle1">Email</Typography>
          <RHFTextField
            className={classes.loginTextInput}
            name="email"
          />
      </div>

      <div>
        <Typography variant="subtitle1">Password</Typography>
        <RHFTextField
          className={classes.loginTextInput}
          name="password"
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={password.onToggle} edge="end">
                  <Iconify
                    icon={password.value ? 'ooui:eye' : 'formkit:eyeclosed'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      </Stack>
      <Link
        component={RouterLink}
        href={paths.auth.amplify.forgotPassword}
        variant="body2"
        color={theme.palette.text.secondary}
        underline="always"
        sx={{ alignSelf: 'flex-end' }}
      >
        Forgot password?
      </Link>
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          color: theme.palette.background.default,
          backgroundColor: theme.palette.background.primary,
          borderColor: theme.palette.border.primaryAccent1,
          borderWidth: '2px',
          '&:hover': {
            backgroundColor: theme.palette.background.primaryAccent1,
          },
        }}
      >
        Continue
      </LoadingButton>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
      <Modal
        open={showOrganizationSelection}
        onClose={() => setShowOrganizationSelection(false)}
        aria-labelledby="account-selection-modal"
        aria-describedby="modal-for-selecting-account"
        style={{width:'100%',height:'100%',display:'flex' ,justifyContent:'center',alignItems:'center'}}
      >
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          width="90%"
          maxWidth="580px"
          maxHeight="460px"
          height="fitContent"
          bgcolor={theme.palette.background.default}
          border="1px solid theme.palette.border.primaryBlack3"
          borderRadius="16px"
          padding="25px"
        >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              height="35px"
              marginBottom="50px"
              marginTop="20px"
              width="100%"
            >
              <Typography variant="h4" fontSize={28} fontWeight="bold"> Choose Organization </Typography>
              <IconButton onClick={() => setShowOrganizationSelection(false)} sx={{ alignSelf: 'flex-end', color: theme.palette.text.primary }}>
                <ClearRounded />
              </IconButton>
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              spacing={1}
              width="100%"
              sx={{
                maxHeight: '286px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '0px',
                  background: 'transparent',
                },
              }}
            >
              { organizationList.map((org, index) => (
              <Stack
                 direction="row"
                 alignItems="center"
                 spacing={2}
                 sx={{
                   display: 'block',
                   marginBottom: '5px',
                   width: '100%',
                   height: '64px',
                   border: 'none',
                 }}
               >
                <Button
                  onClick={() => {
                    setOrganizationId(org.organization_id);
                    handleOrganizationSelection(org.organization_id);
                  }}
                  variant="outlined"
                  sx={{
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar src={org.logo_url} alt="Account Logo" sx={{ width: 44, height: 44,fontSize: 'inherit'}} />
                    <Typography variant="subtitle1" fontWeight="600">{org.name}</Typography>
                  </Stack>
                  <Iconify icon="bi:arrow-right" style={{ marginLeft: 'auto' }} />
                </Button>
              </Stack>
              ))}
            </Stack>
        </Stack>
      </Modal>
    </FormProvider>
  );
}
